import { useMemo, useState } from 'react';
import { ApiError } from '../lib/api';
import { hasString, useI18n } from '../lib/i18n';
import { useApiError } from '../lib/useApiError';
import { parseMoneyToTetri } from '../lib/money';
import { formatGel, formatMultiplier, formatRate } from '../lib/format';
import { useSettings, useTiers, useUpdateSettings, useUpdateTier } from '../lib/queries';
import { can, useSession } from '../lib/session';
import type { SettingRow } from '../lib/types';
import {
  Alert, Button, Card, HelpNote, Label, MoneyInput, Pill, SectionTitle, Select, Skeleton, TierBadge,
} from '../components/ui';

/**
 * The setting's own key, made readable, for anything the dictionary has not
 * caught up with yet. Every key in the registry is translated; this keeps a
 * newly added one visible instead of blank until its words are written.
 */
const humanise = (key: string): string =>
  key.split('.').slice(1).join('.').replace(/_/g, ' ').replace(/\b\w/, (c) => c.toUpperCase());

function Control({
  setting, value, onChange,
}: { setting: SettingRow; value: unknown; onChange: (next: unknown) => void }) {
  const { t } = useI18n();

  if (setting.control === 'boolean') {
    return (
      <button
        role="switch"
        aria-checked={value === true}
        onClick={() => onChange(!value)}
        className="grid h-11 w-11 shrink-0 place-items-center rounded-lg"
      >
        {/* The track stays 24px tall; the button around it is the thumb target. */}
        <span
          className={`relative block h-6 w-11 rounded-full transition-colors ${
            value ? 'bg-brand' : 'bg-muted'
          }`}
        >
          {/*
            Positioned by `left` alone. Anchoring with a transform instead left
            the knob offset twice — once by its static position and again by the
            translate — which pushed it clean off the right of the track.
          */}
          <span
            className={`absolute top-0.5 size-5 rounded-full bg-ink transition-[left] duration-150 ${
              value ? 'left-[1.375rem]' : 'left-0.5'
            }`}
          />
        </span>
      </button>
    );
  }

  if (setting.control === 'select' && setting.options) {
    return (
      <div className="w-56">
        <Select value={String(value ?? '')} onChange={(event) => onChange(event.target.value)}>
          {setting.options.map((option) => (
            <option key={option} value={option}>{option}</option>
          ))}
        </Select>
      </div>
    );
  }

  if (setting.control === 'money') {
    return (
      <div className="w-36">
        <MoneyInput
          value={value === null ? '' : String(Number(value) / 100)}
          placeholder={t('settings.none')}
          onChange={(event) => {
            const text = event.target.value.trim();
            onChange(text === '' ? null : parseMoneyToTetri(text));
          }}
        />
      </div>
    );
  }

  if (setting.control === 'percent') {
    return (
      <div className="relative w-28">
        <input
          inputMode="decimal"
          value={value === null ? '' : String(Number(value) / 100)}
          onChange={(event) => {
            const text = event.target.value.trim();
            onChange(text === '' ? null : parseMoneyToTetri(text));
          }}
          className="tabular w-full rounded-xl border border-line bg-raised py-2.5 pl-3.5 pr-7 text-right text-ink focus:border-brand focus:outline-none"
        />
        <span className="pointer-events-none absolute right-3 top-1/2 -translate-y-1/2 text-sm text-ink-faint">%</span>
      </div>
    );
  }

  return (
    <input
      value={value === null || value === undefined ? '' : String(value)}
      inputMode={setting.control === 'text' ? 'text' : 'numeric'}
      onChange={(event) => {
        const text = event.target.value;
        if (setting.control === 'text') { onChange(text); return; }
        onChange(text.trim() === '' ? null : Number(text));
      }}
      className={`rounded-xl border border-line bg-raised px-3.5 py-2.5 text-ink focus:border-brand focus:outline-none ${
        setting.control === 'text' ? 'w-56' : 'tabular w-28 text-right'
      }`}
    />
  );
}

function TiersEditor() {
  const { t } = useI18n();
  const { data, isPending } = useTiers();
  const updateTier = useUpdateTier();
  const { data: session } = useSession();
  const mayConfigure = can(session, 'loyalty.configure');
  const [draft, setDraft] = useState<Record<string, Record<string, unknown>>>({});

  if (isPending) return <Skeleton className="h-48" />;

  return (
    <Card>
      <SectionTitle>{t('settings.tiers')}</SectionTitle>
      <div className="space-y-3">
        {data?.items.map((tier) => {
          const edits = draft[tier.id] ?? {};
          const dirty = Object.keys(edits).length > 0;
          const field = (key: keyof typeof tier) => edits[key] ?? tier[key];

          return (
            <div key={tier.id} className="rounded-xl border border-line bg-raised/40 p-3">
              <div className="flex items-center justify-between gap-3">
                <div className="flex items-center gap-2">
                  <TierBadge code={tier.code} />
                  {tier.isPermanent && <Pill>{t('settings.permanent')}</Pill>}
                </div>
                {dirty && mayConfigure && (
                  <Button
                    size="sm"
                    loading={updateTier.isPending}
                    onClick={() => updateTier.mutate({ id: tier.id, ...edits }, {
                      onSuccess: () => setDraft((prev) => {
                        const next = { ...prev };
                        delete next[tier.id];
                        return next;
                      }),
                    })}
                  >{t('common.save')}</Button>
                )}
              </div>

              <div className="mt-3 grid gap-3 sm:grid-cols-3">
                <div className="flex flex-col gap-1.5">
                  <Label>{t('settings.fromLifetime')}</Label>
                  <MoneyInput
                    disabled={!mayConfigure}
                    value={String(Number(field('thresholdTetri')) / 100)}
                    onChange={(event) => setDraft((prev) => ({
                      ...prev,
                      [tier.id]: {
                        ...prev[tier.id],
                        thresholdTetri: parseMoneyToTetri(event.target.value),
                      },
                    }))}
                  />
                </div>
                <div className="flex flex-col gap-1.5">
                  <Label>{t('settings.cashbackPercent')}</Label>
                  <input
                    disabled={!mayConfigure}
                    inputMode="decimal"
                    value={String(Number(field('cashbackRateBp')) / 100)}
                    onChange={(event) => setDraft((prev) => ({
                      ...prev,
                      [tier.id]: {
                        ...prev[tier.id],
                        cashbackRateBp: Math.round(Number(event.target.value.replace(',', '.')) * 100),
                      },
                    }))}
                    className="tabular rounded-xl border border-line bg-raised px-3.5 py-2.5 text-right text-ink focus:border-brand focus:outline-none disabled:opacity-50"
                  />
                </div>
                <div className="flex flex-col gap-1.5">
                  <Label>{t('settings.pointsMultiplier')}</Label>
                  <input
                    disabled={!mayConfigure}
                    inputMode="decimal"
                    value={String(Number(field('pointsMultiplierMilli')) / 1000)}
                    onChange={(event) => setDraft((prev) => ({
                      ...prev,
                      [tier.id]: {
                        ...prev[tier.id],
                        pointsMultiplierMilli: Math.round(Number(event.target.value.replace(',', '.')) * 1000),
                      },
                    }))}
                    className="tabular rounded-xl border border-line bg-raised px-3.5 py-2.5 text-right text-ink focus:border-brand focus:outline-none disabled:opacity-50"
                  />
                </div>
              </div>

              <p className="mt-2 text-[0.7rem] text-ink-faint">
                {t('settings.tierSummary', {
                  threshold: formatGel(Number(field('thresholdTetri'))),
                  rate: formatRate(Number(field('cashbackRateBp'))),
                  multiplier: formatMultiplier(Number(field('pointsMultiplierMilli'))),
                })}
              </p>
            </div>
          );
        })}
      </div>
    </Card>
  );
}

export function SettingsPage() {
  const { t } = useI18n();
  const settingLabel = (key: string) => {
    const k = `setting.${key}`;
    return hasString(k) ? t(k) : humanise(key);
  };
  /** A sentence of plain explanation, where one has been written. */
  const settingHelp = (key: string): string | null => {
    const k = `settingHelp.${key}`;
    return hasString(k) ? t(k) : null;
  };
  const categoryTitle = (category: string) => {
    const k = `settingCat.${category}`;
    return hasString(k) ? t(k) : category;
  };
  const { data, isPending } = useSettings();
  const update = useUpdateSettings();
  const { data: session } = useSession();
  const [draft, setDraft] = useState<Record<string, unknown>>({});
  const [showAdvanced, setShowAdvanced] = useState(false);

  const mayUpdate = can(session, 'settings.update');
  const errorText = useApiError();
  const error = update.error instanceof ApiError ? update.error : null;

  const grouped = useMemo(() => {
    const map = new Map<string, SettingRow[]>();
    for (const setting of data?.items ?? []) {
      if (setting.advanced && !showAdvanced) continue;
      const bucket = map.get(setting.category) ?? [];
      bucket.push(setting);
      map.set(setting.category, bucket);
    }
    return map;
  }, [data, showAdvanced]);

  const dirtyKeys = Object.keys(draft);

  if (isPending) {
    return <div className="mx-auto max-w-4xl space-y-3">{[0, 1, 2].map((i) => <Skeleton key={i} className="h-40" />)}</div>;
  }

  return (
    <div className="mx-auto max-w-4xl pb-24">
      <header className="mb-5 flex flex-wrap items-end justify-between gap-3">
        <div>
          <h1 className="font-display text-[1.75rem] font-bold leading-tight tracking-tight text-ink sm:text-[2rem]">{t('settings.title')}</h1>
          <p className="mt-1.5 text-sm text-ink-dim">
            {t('settings.subtitle', { version: data?.rulesVersion ?? '' })}
          </p>
        </div>
        <label className="flex min-h-11 cursor-pointer items-center gap-2 text-xs text-ink-dim">
          <input
            type="checkbox"
            checked={showAdvanced}
            onChange={(event) => setShowAdvanced(event.target.checked)}
            className="size-5 accent-[var(--color-brand)]"
          />{t('settings.showAdvanced')}</label>
      </header>

      {error && <div className="mb-4"><Alert>{errorText(error) ?? error.message}</Alert></div>}

      {update.isSuccess && dirtyKeys.length === 0 && (
        <div className="mb-4">
          <Alert tone="success">
            {t('settings.saved')}
            {update.data?.rulesVersion && (
              <span className="ml-1">
                {t('settings.rulesChanged', { version: update.data.rulesVersion })}
              </span>
            )}
          </Alert>
        </div>
      )}

      <div className="space-y-4">
        <TiersEditor />

        {[...grouped.entries()].map(([category, settings]) => (
          <Card key={category}>
            <SectionTitle>{categoryTitle(category)}</SectionTitle>
            <div className="space-y-3">
              {settings.map((setting) => {
                const value = setting.key in draft ? draft[setting.key] : setting.value;
                const changed = setting.key in draft;
                return (
                  <div
                    key={setting.key}
                    className={`flex flex-wrap items-center justify-between gap-3 rounded-xl px-3 py-2.5 transition-colors ${
                      changed ? 'bg-brand-wash' : ''
                    }`}
                  >
                    <div className="min-w-0 flex-1">
                      <div className="flex flex-wrap items-center gap-2">
                        <span className="text-sm text-ink">{settingLabel(setting.key)}</span>
                        {settingHelp(setting.key) && <HelpNote text={settingHelp(setting.key)!} />}
                        {showAdvanced && setting.versioned && <Pill>{t('settings.versioned')}</Pill>}
                        {setting.advanced && <Pill tone="warning">{t('settings.advancedPill')}</Pill>}
                      </div>
                      {/*
                        Behind the advanced switch, where it is useful and not
                        in the way. Fifty-odd keys like
                        loyalty.min_order_for_cashback_tetri under every row
                        read as somebody's database, not as a settings screen.
                      */}
                      {showAdvanced && (
                        <div className="tabular mt-0.5 text-[0.65rem] text-ink-faint">{setting.key}</div>
                      )}
                    </div>
                    <div className={mayUpdate ? '' : 'pointer-events-none opacity-50'}>
                      <Control
                        setting={setting}
                        value={value}
                        onChange={(next) => setDraft((prev) => {
                          const copy = { ...prev };
                          if (JSON.stringify(next) === JSON.stringify(setting.value)) delete copy[setting.key];
                          else copy[setting.key] = next;
                          return copy;
                        })}
                      />
                    </div>
                  </div>
                );
              })}
            </div>
          </Card>
        ))}
      </div>

      {dirtyKeys.length > 0 && mayUpdate && (
        <div className="fixed inset-x-0 bottom-0 z-20 border-t border-line bg-surface/95 px-4 pb-[max(0.75rem,env(safe-area-inset-bottom))] pt-3 backdrop-blur-sm">
          <div className="mx-auto flex max-w-4xl items-center gap-3">
            <span className="flex-1 text-sm text-ink-dim">
              {t('settings.pending', { count: dirtyKeys.length })}
            </span>
            <Button variant="ghost" onClick={() => setDraft({})}>{t('settings.discard')}</Button>
            <Button
              loading={update.isPending}
              onClick={() => update.mutate(draft, { onSuccess: () => setDraft({}) })}
            >{t('common.save')}</Button>
          </div>
        </div>
      )}
    </div>
  );
}
